import React from "react";
import "./../css/Import.css";
import { useSnackbar } from "notistack";

interface ImportProps {
  isOpen: boolean;
  onClose: () => void;
  onImportSuccess: () => void;
}

const Import: React.FC<ImportProps> = ({ isOpen, onClose, onImportSuccess }) => {
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null);
  const [previewData, setPreviewData] = React.useState<any[]>([]);
  const [isProcessing, setIsProcessing] = React.useState(false);
  const [importMode, setImportMode] = React.useState<'csv' | 'photo'>('photo');
  const [photoPreview, setPhotoPreview] = React.useState<string | null>(null);
  const [photoZoom, setPhotoZoom] = React.useState(1);
  const [manualEntry, setManualEntry] = React.useState({
    title: '',
    datum: '',
    startzeit: '00:00',
    endzeit: '23:59',
    bgColor: '#3788d8',
    ganzTaegig: 0,
    erinnern: 0
  });
  const { enqueueSnackbar } = useSnackbar();
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const photoInputRef = React.useRef<HTMLInputElement>(null);

  if (!isOpen) return null;

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setSelectedFile(file);
    parseFile(file);
  };

  const parseFile = async (file: File) => {
    setIsProcessing(true);
    try {
      const text = await file.text();
      const lines = text.split("\n").filter((line) => line.trim());

      if (lines.length === 0) {
        enqueueSnackbar("Datei ist leer", { variant: "error" });
        setIsProcessing(false);
        return;
      }

      // Parse CSV (erwarte: Titel,Datum,Startzeit,Endzeit,Farbe,Ganztägig,erinnern)
      const data = [];

      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(",").map((v) => v.trim());
        if (values.length >= 2) {
          const termin: any = {
            title: values[0] || "Ohne Titel",
            datum: values[1] || "",
            startzeit: values[2] || "00:00",
            endzeit: values[3] || "23:59",
            bgColor: values[4] || "#3788d8",
            ganzTaegig: values[5] === "1" || values[5]?.toLowerCase() === "ja" ? 1 : 0,
            erinnern: values[6] === "1" || values[6]?.toLowerCase() === "ja" ? 1 : 0
          };
          data.push(termin);
        }
      }

      setPreviewData(data);
      enqueueSnackbar(`${data.length} Termine gefunden`, { variant: "info" });
    } catch (error) {
      console.error("Fehler beim Parsen:", error);
      enqueueSnackbar("Fehler beim Lesen der Datei", { variant: "error" });
    }
    setIsProcessing(false);
  };

  const handleImport = async () => {
    if (previewData.length === 0) {
      enqueueSnackbar("Keine Daten zum Importieren", { variant: "warning" });
      return;
    }

    setIsProcessing(true);

    try {
      let successCount = 0;
      let errorCount = 0;

      for (const termin of previewData) {
        const startTime = `${termin.datum}T${termin.startzeit}`;
        const endTime = `${termin.datum}T${termin.endzeit}`;

        const response = await fetch(
          "https://norbertspringer.de/calendar/api/setTermin.php",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              idUser: 1,
              startTime,
              endTime,
              title: termin.title,
              bgColor: termin.bgColor,
              wiederholen: "nie",
              ganzTaegig: termin.ganzTaegig,
              erinnern: 0,
            }),
          }
        );

        if (response.ok) {
          successCount++;
        } else {
          errorCount++;
        }
      }

      enqueueSnackbar(
        `${successCount} Termine importiert ${errorCount > 0 ? `, ${errorCount} Fehler` : ""}`,
        { variant: successCount > 0 ? "success" : "error" }
      );

      if (successCount > 0) {
        onImportSuccess();
        handleClose();
      }
    } catch (error) {
      console.error("Import-Fehler:", error);
      enqueueSnackbar("Fehler beim Import", { variant: "error" });
    }

    setIsProcessing(false);
  };

  const handleClose = () => {
    setSelectedFile(null);
    setPreviewData([]);
    setIsProcessing(false);
    setImportMode('csv');
    setPhotoPreview(null);
    setPhotoZoom(1);
    setManualEntry({
      title: '',
      datum: '',
      startzeit: '00:00',
      endzeit: '23:59',
      bgColor: '#3788d8',
      ganzTaegig: 0,
      erinnern: 0
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    if (photoInputRef.current) {
      photoInputRef.current.value = "";
    }
    onClose();
  };

  const handlePhotoSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      enqueueSnackbar('Bitte wählen Sie eine Bilddatei', { variant: 'error' });
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setPhotoPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleAddManualEntry = () => {
    if (!manualEntry.title || !manualEntry.datum) {
      enqueueSnackbar('Titel und Datum sind erforderlich', { variant: 'warning' });
      return;
    }

    const newEntry = { ...manualEntry };
    setPreviewData([...previewData, newEntry]);
    
    // Reset form but keep datum for next entry
    setManualEntry({
      ...manualEntry,
      title: '',
      startzeit: '00:00',
      endzeit: '23:59',
      bgColor: '#3788d8',
      ganzTaegig: 0,
        erinnern: 0
    });

    enqueueSnackbar('Termin hinzugefügt', { variant: 'success' });
  };

  const handleRemoveEntry = (index: number) => {
    setPreviewData(previewData.filter((_, i) => i !== index));
  };

  const downloadTemplate = () => {
    const csv = `Titel,Datum,Startzeit,Endzeit,Farbe,Ganztägig,Erinnern
Meeting,2026-01-15,10:00,11:00,#3788d8,0,0
Geburtstag,2026-02-20,00:00,23:59,#f39c12,1,1
Urlaub,2026-03-10,00:00,23:59,#26a65b,1,0`;

    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "termine_vorlage.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="import-overlay" onClick={handleClose}>
      <div className="import-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="import-header">
          <h2>Termine importieren</h2>
          <button className="close-button" onClick={handleClose}>
            ✕
          </button>
        </div>

        <div className="import-content">
          <div className="import-mode-tabs">
            
            <button
              className={`mode-tab ${importMode === 'photo' ? 'active' : ''}`}
              onClick={() => setImportMode('photo')}
            >
              📸 Foto
            </button><button
              className={`mode-tab ${importMode === 'csv' ? 'active' : ''}`}
              onClick={() => setImportMode('csv')}
            >
              📄 CSV-Datei
            </button>
          </div>

          {importMode === 'csv' ? (
            <>
              <div className="import-info">
                <p>
                  Laden Sie eine CSV-Datei mit Ihren Terminen hoch. Die Datei sollte
                  folgendes Format haben:
                </p>
                <code>Titel,Datum,Startzeit,Endzeit,Farbe,Ganztägig</code>
                <button className="template-button" onClick={downloadTemplate}>
                  📥 Vorlage herunterladen
                </button>
              </div>

              <div className="file-upload-section">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.txt"
                  onChange={handleFileSelect}
                  className="file-input"
                  id="file-upload"
                />
                <label htmlFor="file-upload" className="file-upload-label">
                  📄 Datei auswählen
                </label>
                {selectedFile && (
                  <span className="file-name">{selectedFile.name}</span>
                )}
              </div>
            </>
          ) : (
            <div className="photo-import-section">
              <div className="photo-upload-area">
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoSelect}
                  className="file-input"
                  id="photo-upload"
                />
                <label htmlFor="photo-upload" className="photo-upload-label">
                  {photoPreview ? (
                    <div className="photo-preview-wrapper">
                      <div className="photo-zoom-controls">
                        <button
                          type="button"
                          className="zoom-button"
                          onClick={(e) => {
                            e.preventDefault();
                            setPhotoZoom(Math.max(1, photoZoom - 0.25));
                          }}
                          disabled={photoZoom <= 1}
                        >
                          −
                        </button>
                        <span className="zoom-level">{Math.round(photoZoom * 100)}%</span>
                        <button
                          type="button"
                          className="zoom-button"
                          onClick={(e) => {
                            e.preventDefault();
                            setPhotoZoom(Math.min(3, photoZoom + 0.25));
                          }}
                          disabled={photoZoom >= 3}
                        >
                          +
                        </button>
                      </div>
                      <div className="photo-preview-container">
                        <img 
                          src={photoPreview} 
                          alt="Kalender" 
                          className="photo-preview"
                          style={{ transform: `scale(${photoZoom})` }}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="photo-placeholder">
                      <span className="photo-icon">📸</span>
                      <span>Foto hochladen</span>
                      <span className="photo-hint">Klicken Sie hier um ein Foto auszuwählen</span>
                    </div>
                  )}
                </label>
              </div>

              <div className="manual-entry-form">
                <h4>Termin hinzufügen</h4>
                <div className="form-group">
                  <label>Titel *</label>
                  <input
                    type="text"
                    value={manualEntry.title}
                    onChange={(e) => setManualEntry({ ...manualEntry, title: e.target.value })}
                    placeholder="z.B. Meeting"
                  />
                </div>
                <div className="form-group">
                  <label>Datum *</label>
                  <input
                    type="date"
                    value={manualEntry.datum}
                    onChange={(e) => setManualEntry({ ...manualEntry, datum: e.target.value })}
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Von</label>
                    <input
                      type="time"
                      value={manualEntry.startzeit}
                      onChange={(e) => setManualEntry({ ...manualEntry, startzeit: e.target.value })}
                      disabled={manualEntry.ganzTaegig === 1}
                    />
                  </div>
                  <div className="form-group">
                    <label>Bis</label>
                    <input
                      type="time"
                      value={manualEntry.endzeit}
                      onChange={(e) => setManualEntry({ ...manualEntry, endzeit: e.target.value })}
                      disabled={manualEntry.ganzTaegig === 1}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Kategorie</label>
                    <select
                className="dialog-input"
                name="bgColor"
                id="bgColor"
                value={manualEntry?.bgColor}
                onChange={(e) => setManualEntry({ ...manualEntry, bgColor: e.target.value })}
              >
                <option value="#3788d8">Allgemein</option>
                <option value="#26a65b">Feiertag</option>
                <option value="#f39c12">Geburtstag</option>
                <option value="#e74c3c">Wichtig</option>
                <option value="#8e44ad">Persönlich</option>
                <option value="#d35400">Arbeit</option>
                <option value="#2c3e50">Schicht</option>
              </select>
                  
                </div>
                <div className="form-row">
                <div className="form-group checkbox-group">
                  <label style={{marginLeft: '50%'}}>
                    <input
                      type="checkbox"
                      checked={manualEntry.ganzTaegig === 1}
                      onChange={(e) => setManualEntry({ ...manualEntry, ganzTaegig: e.target.checked ? 1 : 0 })}
                    />
                    Ganztägig
                  </label>
                </div>
                  <div className="form-group checkbox-group">
                  <label style={{marginLeft: '25%'}}>
                    <input
                      type="checkbox"
                      checked={manualEntry.erinnern === 1}
                      onChange={(e) => setManualEntry({ ...manualEntry, erinnern: e.target.checked ? 1 : 0 })}
                    />
                        Erinnern
                  </label>
                </div>
                </div>
                <button className="add-entry-button" onClick={handleAddManualEntry}>
                  ➕ Hinzufügen
                </button>
              </div>
            </div>
          )}

          {previewData.length > 0 && (
            <div className="preview-section">
              <h3>Vorschau ({previewData.length} Termine)</h3>
              <div className="preview-list">
                {previewData.map((termin, index) => (
                  <div key={index} className="preview-item">
                    <div
                      className="preview-color-dot"
                      style={{ backgroundColor: termin.bgColor }}
                    />
                    <div className="preview-content">
                      <div className="preview-title">{termin.title}</div>
                      <div className="preview-details">
                        📅 {termin.datum} • ⏰{" "}
                        {termin.ganzTaegig
                          ? "Ganztägig"
                          : `${termin.startzeit} - ${termin.endzeit}`}
                      </div>
                    </div>
                    {importMode === 'photo' && (
                      <button 
                        className="remove-entry-button"
                        onClick={() => handleRemoveEntry(index)}
                        title="Entfernen"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="import-footer">
          <button className="cancel-button" onClick={handleClose}>
            Abbrechen
          </button>
          <button
            className="import-button"
            onClick={handleImport}
            disabled={previewData.length === 0 || isProcessing}
          >
            {isProcessing ? "Importiere..." : `${previewData.length} Termine importieren`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Import;
